import { NgForOf } from '@angular/common';
import { Component, EventEmitter, inject, Output, signal } from '@angular/core';

import { IonItem, IonItemDivider, IonLabel, IonList, IonSpinner } from '@ionic/angular/standalone';

import { Address, GeocodingService } from '@guardfinder/address';

import { GeolocationPosition, GeolocationService } from '../../services';
import { SvgIconComponent } from '../icons';

@Component({
    selector: 'lib-use-my-location',
    imports: [IonItem, IonLabel, SvgIconComponent, IonSpinner, IonList, NgForOf, IonItemDivider, SvgIconComponent],
    templateUrl: './use-my-location-component.component.html'
})
export class UseMyLocationComponent {
    @Output() addressFound = new EventEmitter<Address>();

    addressesFound = signal<Address[]>([]);
    showUseMyLocation = signal(true);
    isLoading = signal(false);
    errorMessage = signal<string | undefined>(undefined);

    geolocationService: GeolocationService = inject(GeolocationService);
    geocodingService: GeocodingService = inject(GeocodingService);

    /**
     * Handle the click event on the "Use my location" button
     * Uses the injected GeolocationService to get the current position
     */
    async onUseMyLocationClick() {
        if (this.isLoading()) {
            return; // Prevent multiple clicks
        }

        this.isLoading.set(true);
        this.errorMessage.set(undefined);

        try {
            const position = await this.geolocationService.getCurrentPosition({
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            });
            await this.onLocationFound(position);
        } catch (error: unknown) {
            if (error instanceof Error) {
                this.errorMessage.set(error.message);
                return;
            }
            this.errorMessage.set('Unknown error');
        } finally {
            this.isLoading.set(false);
        }
    }

    async onLocationFound(position: GeolocationPosition): Promise<void> {
        try {
            const addresses = await this.geocodingService.getAddressesByCoordinates(
                position.coords.latitude,
                position.coords.longitude
            );

            this.addressesFound.set(addresses);
            this.showUseMyLocation.set(false);
        } catch (error) {
            if (error instanceof Error) {
                this.errorMessage.set(error.message);
                return;
            }
            this.errorMessage.set('Unknown error');
        }
    }

    onAddressSelect(address: Address) {
        this.addressFound.emit(address);
        this.addressesFound.set([]);
        this.showUseMyLocation.set(false);
    }
}
